/**
 * useAccountTransactionsCheck Hook
 * 
 * Checks if accounts have been used in transactions (real-time listener).
 * Used to determine if accounts can be deleted or archived.
 */

import { useState, useEffect } from "react";
import {
  collection,
  query,
  onSnapshot,
  QuerySnapshot,
  FirestoreError,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Account } from "../../../types";

/**
 * Hook to check if accounts have transactions
 * @param workspaceId - Workspace ID
 * @param accounts - Array of accounts to check
 * @returns Map of account IDs to boolean (true if account has transactions)
 */
export function useAccountTransactionsCheck(
  workspaceId: string | undefined,
  accounts: Account[]
): Record<string, boolean> {
  const [accountHasTransactions, setAccountHasTransactions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!workspaceId || accounts.length === 0) {
      setAccountHasTransactions({});
      return;
    }

    // Set up real-time listener for all transactions
    const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
    const transactionsQuery = query(transactionsRef);

    const unsubscribe = onSnapshot(
      transactionsQuery,
      (snapshot: QuerySnapshot) => {
        // Create a set of account IDs that have transactions
        const accountsWithTransactions = new Set<string>();

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          
          // Check accountId (for income/expense/adjustment)
          if (data.accountId) {
            accountsWithTransactions.add(data.accountId);
          }
          
          // Check fromAccountId and toAccountId (for transfers)
          if (data.fromAccountId) {
            accountsWithTransactions.add(data.fromAccountId);
          }
          if (data.toAccountId) {
            accountsWithTransactions.add(data.toAccountId);
          }
        });

        // Build the map for all accounts
        const hasTransactionsMap: Record<string, boolean> = {};
        accounts.forEach((account) => {
          hasTransactionsMap[account.id] = accountsWithTransactions.has(account.id);
        });

        setAccountHasTransactions(hasTransactionsMap);
      },
      (error: FirestoreError) => {
        console.error("Error listening to transactions:", error);
        // On error, assume all accounts have transactions (safer)
        const hasTransactionsMap: Record<string, boolean> = {};
        accounts.forEach((account) => {
          hasTransactionsMap[account.id] = true;
        });
        setAccountHasTransactions(hasTransactionsMap);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [workspaceId, accounts]);

  return accountHasTransactions;
}
